<template lang="pug">
.c-help-admin
  teleport(to="#resource-actions-portal" v-if="mounted")
    el-button(type="primary" @click="open()")
      fai(type="fal" icon="plus-circle")
      span Add help link

  el-table(:data="links" v-loading="loading" style="width: 100%")
    el-table-column(label="Order" prop="sortOrder" width="80")
    el-table-column(label="Placement" prop="placement" width="140")
    el-table-column(label="Title" prop="title" min-width="160")
    el-table-column(label="Description" prop="description" min-width="200")
    el-table-column(label="URL" prop="url" min-width="220")
    el-table-column(label="Icon" prop="icon" width="130")
    el-table-column(label="Enabled" width="90")
      template(#default="{ row }")
        span.crm-badge(:class="row.isEnabled ? 'crm-badge--teal' : 'crm-badge--gray'")
          | {{ row.isEnabled ? 'Yes' : 'No' }}
    el-table-column(width="170")
      template(#default="{ row }")
        el-button(size="small" text @click="open(row)") Edit
        el-button(size="small" text type="danger" @click="remove(row)") Delete

  el-dialog(v-model="dialog" :title="form.id ? 'Edit help link' : 'Add help link'" width="520px")
    el-form(label-position="top")
      el-form-item(label="Placement")
        el-select(v-model="form.placement" allow-create filterable)
          el-option(label="DASHBOARD" value="DASHBOARD")
      el-form-item(label="Title")
        el-input(v-model="form.title")
      el-form-item(label="Description")
        el-input(v-model="form.description")
      el-form-item(label="URL (http/https)")
        el-input(v-model="form.url")
      el-form-item(label="Icon")
        el-select(v-model="form.icon" allow-create filterable)
          el-option(v-for="i in icons" :key="i" :label="i" :value="i")
      el-form-item(label="Sort order")
        el-input-number(v-model="form.sortOrder" :min="0")
      el-form-item(label="Enabled")
        el-switch(v-model="form.isEnabled")
    template(#footer)
      el-button(@click="dialog = false") Cancel
      el-button(type="primary" :loading="saving" @click="save") Save
</template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive, ref,
} from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import { findAllHelpLinks, mutateHelpLink, removeHelpLink } from '@/graphs/gql';

const empty = () => ({
  id: undefined as string | undefined,
  placement: 'DASHBOARD',
  title: '',
  description: '',
  url: '',
  icon: 'help-circle',
  sortOrder: 0,
  isEnabled: true,
});

export default defineComponent({
  name: 'HelpLinksAdmin',
  setup() {
    const { makeGraphRequest } = globalShared();
    const mounted = ref(false);
    const loading = ref(false);
    const saving = ref(false);
    const links = ref<any[]>([]);
    const dialog = ref(false);
    const form = reactive<any>(empty());
    const icons = ['rocket', 'lightbulb', 'help-circle', 'book-open', 'message-circle'];

    async function load() {
      loading.value = true;
      try {
        const response = await makeGraphRequest('query', findAllHelpLinks, {});
        if (response.errors) throw new Error(response.errors[0].message);
        links.value = response.data.findAllHelpLinks;
      } catch (e: any) {
        ElMessage.error(e.message || 'Could not load help links');
      } finally {
        loading.value = false;
      }
    }

    function open(row?: any) {
      Object.assign(form, empty(), row ? { ...row, description: row.description || '' } : {});
      dialog.value = true;
    }

    async function run(doc: any, variables: any, success: string) {
      saving.value = true;
      try {
        const response = await makeGraphRequest('mutation', doc, variables);
        if (response.errors) throw new Error(response.errors[0].message);
        ElMessage.success(success);
        await load();
        return true;
      } catch (e: any) {
        ElMessage.error(e.message || 'Something went wrong');
        return false;
      } finally {
        saving.value = false;
      }
    }

    async function save() {
      // Send only HelpLinkInput fields: the row loaded into the form also carries __typename.
      const input: any = Object.fromEntries(Object.keys(empty()).map((key) => [key, form[key]]));
      if (!input.id) delete input.id;
      if (await run(mutateHelpLink, { input }, 'Saved')) dialog.value = false;
    }

    async function remove(row: any) {
      await ElMessageBox.confirm(`Delete "${row.title}"?`, 'Delete help link', {
        type: 'warning', confirmButtonText: 'Delete',
      });
      run(removeHelpLink, { helpLinkId: row.id }, 'Deleted');
    }

    onMounted(() => {
      mounted.value = true;
      load();
    });

    return {
      mounted, loading, saving, links, dialog, form, icons, open, save, remove,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/views/manage-users-and-organizations/crm-list';

@include crm-cells;
@include crm-table('c-help-admin');

.c-help-admin :deep(.el-table__row) {
  cursor: default;
}
</style>
